/**
 * EventTracker — Unified event tracking for product analytics
 *
 * Each function routes through AnalyticsStore.trackEvent with BEHAVIORAL
 * category, which sends to PostHog + Datadog via routeEventToAnalyticsSystems.
 */

import { PostHogIntegration } from '@src/monitoring/utils/PostHogIntegration';
import { LogManager } from '@src/utility/Logger';

import type { AnalyticsStore } from '@src/stores-v2/AnalyticsStore';

const logger = LogManager.getLogger('analytics:EventTracker');

let _analyticsStore: AnalyticsStore | null = null;
let _organizationId: string | undefined;
let _organizationName: string | undefined;

export function initEventTracker(analyticsStore: AnalyticsStore): void {
  _analyticsStore = analyticsStore;
}

export function setOrganizationContext(orgId: string, orgName: string): void {
  _organizationId = orgId;
  _organizationName = orgName;
  // Super properties attach org to every PostHog event (autocapture, pageview, etc.)
  PostHogIntegration.setContext({
    organization_id: orgId,
    organization_name: orgName,
  });
  // Group identification unlocks org-level analytics in PostHog UI
  PostHogIntegration.group('organization', orgId, { name: orgName });
}

export function resetOrganizationContext(): void {
  _organizationId = undefined;
  _organizationName = undefined;
  // PostHogIntegration.shutdown() already calls posthog.reset() — no extra unregister needed
}

function track(eventName: string, props: Record<string, unknown>): void {
  logger.debug('firing:', eventName, props);

  const enrichedProps: Record<string, unknown> = {
    ...props,
    ...(_organizationId ? { organization_id: _organizationId } : {}),
    ...(_organizationName ? { organization_name: _organizationName } : {}),
  };

  if (_analyticsStore) {
    _analyticsStore.trackEvent({
      eventName,
      label: eventName,
      extraProperties: enrichedProps,
    });
  } else {
    logger.warn(
      'EventTracker: analyticsStore not initialized, event dropped:',
      eventName,
    );
  }
}

// ─── Clip Producer ───────────────────────────────────────────────

export function trackClipProducerOpened(props: {
  story_id?: string;
  video_id?: string;
}): void {
  track('clip_producer_opened', props);
}

export function trackClipGenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  video_id?: string;
  video_title?: string;
  theme?: string;
  custom_prompt?: string;
  platform?: string;
  duration?: number | null;
  speaker_count?: number;
  goal?: string;
  audience_count?: number;
  template_applied?: boolean;
  template_id?: string;
  template_name?: string;
  selected_topics?: string[];
}): void {
  track('produce_clips', props);
}

export function trackClipGenerationCompleted(props: {
  story_id?: string;
  story_title?: string;
  video_id?: string;
  clips_count: number;
  retry_count: number;
  theme?: string;
  platform?: string;
}): void {
  track('clip_generation_completed', props);
}

export function trackClipGenerationFailed(props: {
  story_id?: string;
  story_title?: string;
  video_id?: string;
  retry_count: number;
  max_retries: number;
  error_message?: string;
  theme?: string;
  platform?: string;
}): void {
  track('clip_generation_failed', props);
}

// ─── Clip Results ────────────────────────────────────────────────

export function trackClipSaved(props: {
  story_id?: string;
  clips_count: number;
  source_platform: string;
  as_reel: boolean;
}): void {
  track('clips_saved', props);
}

export function trackClipsCombined(props: {
  story_id?: string;
  clips_count: number;
  source_platform: string;
}): void {
  track('clips_combined', props);
}

// ─── Clip Share / Edit / Publish / Download (Content Studio) ─────

export function trackClipEditStarted(props: {
  clip_id?: string;
  story_id?: string;
}): void {
  track('clip_edit_started', props);
}

export function trackSafeZoneSelected(props: {
  selection: string;
  channel: string | null;
  format: string | null;
  aspect_ratio?: string;
  clip_id?: string;
  story_id?: string;
}): void {
  track('safe_zone_selected', props);
}

export function trackSafeZoneVariantToggled(props: {
  selection: string;
  variant: string;
  enabled: boolean;
  aspect_ratio?: string;
  clip_id?: string;
  story_id?: string;
}): void {
  track('safe_zone_variant_toggled', props);
}

export function trackClipPublished(props: {
  clip_id?: string;
  story_id?: string;
  source_platform: string;
  resolution?: string;
}): void {
  track('video_published', props);
}

// ─── Multi-variant Publish (PLAT-10009) ──────────────────────────

/** Fires once per publish command on submit (after records created). */
export function trackPublishStarted(props: {
  clip_id?: string;
  story_id?: string;
  group_id: string;
  variant_count: number;
  languages: string[];
  aspect_ratios: string[];
  resolutions: string[];
}): void {
  track('publish_started', props);
}

/** Fires per variant when a render succeeds (from subscription handler). */
export function trackPublishCompleted(props: {
  clip_id?: string;
  story_id?: string;
  group_id: string;
  publish_record_id: string;
  language: string;
  aspect_ratio: string;
  resolution: string;
  duration_ms?: number;
}): void {
  track('publish_completed', props);
}

/** Fires per variant when a render fails (from subscription handler). */
export function trackPublishFailed(props: {
  clip_id?: string;
  story_id?: string;
  group_id: string;
  publish_record_id: string;
  language: string;
  aspect_ratio: string;
  resolution: string;
  error_message?: string;
}): void {
  track('publish_failed', props);
}

export function trackClipDownloaded(props: {
  clip_id?: string;
  story_id?: string;
  source_platform: string;
  dimension?: string;
}): void {
  track('download_clip', props);
}

// ─── Pull Quotes ─────────────────────────────────────────────────

export function trackPullQuoteProducerOpened(props: {
  story_id?: string;
}): void {
  track('pull_quote_producer_opened', props);
}

export function trackPullQuoteGenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  speaker_count?: number;
  selected_language?: string;
  selected_topics?: string[];
}): void {
  track('generate_quotes', props);
}

export function trackPullQuoteGenerationCompleted(props: {
  story_id?: string;
  quotes_count: number;
}): void {
  track('pull_quote_generation_completed', props);
}

export function trackPullQuoteGenerationFailed(props: {
  story_id?: string;
  error_message?: string;
}): void {
  track('pull_quote_generation_failed', props);
}

export function trackPullQuoteSaved(props: {
  story_id?: string;
  card_id?: string;
  has_image: boolean;
}): void {
  track('save_shareable_image', props);
}

export function trackUpdateStandardsApplied(props: {
  surface: 'creator_studio' | 'pull_quotes';
  controlCount: number;
}): void {
  track('update_standards_applied', props);
}

export function trackApplyStandardsApplied(props: {
  surface: 'creator_studio' | 'pull_quotes';
  changedCount: number;
}): void {
  track('apply_standards_applied', props);
}

export function trackPullQuoteDownloaded(props: {
  story_id?: string;
  card_id?: string;
  format?: string;
}): void {
  track('shareable_image_download', props);
}

// ─── Articles (Blog) ─────────────────────────────────────────────

export function trackArticleProducerOpened(props: { story_id?: string }): void {
  track('article_producer_opened', props);
}

export function trackArticleGenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  selected_language?: string;
  goal?: string;
  audience_count?: number;
  brand_voice?: boolean;
  selected_topics?: string[];
}): void {
  track('generate_blog', props);
}

export function trackArticleGenerationCompleted(props: {
  story_id?: string;
}): void {
  track('article_generation_completed', props);
}

export function trackArticleGenerationFailed(props: {
  story_id?: string;
  error_message?: string;
}): void {
  track('article_generation_failed', props);
}

export function trackArticleSaved(props: { story_id?: string }): void {
  track('save_blog', props);
}

export function trackArticleCopied(props: {
  story_id?: string;
  copy_type?: string;
}): void {
  track('copy_blog', props);
}

// ─── Social Posts ────────────────────────────────────────────────

export function trackSocialPostProducerOpened(props: {
  story_id?: string;
}): void {
  track('social_post_producer_opened', props);
}

export function trackSocialPostGenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  selected_language?: string;
  goal?: string;
  audience_count?: number;
  brand_voice?: boolean;
  selected_topics?: string[];
}): void {
  track('generate_social_posts', props);
}

export function trackSocialPostGenerationCompleted(props: {
  story_id?: string;
}): void {
  track('social_post_generation_completed', props);
}

export function trackSocialPostGenerationFailed(props: {
  story_id?: string;
  error_message?: string;
}): void {
  track('social_post_generation_failed', props);
}

export function trackSocialPostSaved(props: { story_id?: string }): void {
  track('save_social_post', props);
}

export function trackSocialPostCopied(props: {
  story_id?: string;
  copy_type?: string;
}): void {
  track('copy_social_post', props);
}

// ─── Reel Producer ──────────────────────────────────────────────

export function trackReelProducerOpened(props: { story_id?: string }): void {
  track('reel_producer_opened', props);
}

export function trackReelOutlineGenerationStarted(props: {
  story_id?: string;
  target_duration: number;
}): void {
  track('reel_outline_generation_started', props);
}

export function trackReelOutlineGenerationCompleted(props: {
  story_id?: string;
  outline_title: string;
  chapter_count: number;
  target_duration: number;
}): void {
  track('reel_outline_generation_completed', props);
}

export function trackReelOutlineGenerationFailed(props: {
  story_id?: string;
  error_message?: string;
}): void {
  track('reel_outline_generation_failed', props);
}

export function trackReelClipGenerationStarted(props: {
  story_id?: string;
  outline_title: string;
  chapter_count: number;
}): void {
  track('reel_clip_generation_started', props);
}

export function trackReelClipGenerationCompleted(props: {
  story_id?: string;
  clips_count: number;
  chapter_count: number;
}): void {
  track('reel_clip_generation_completed', props);
}

export function trackReelClipGenerationFailed(props: {
  story_id?: string;
  error_message?: string;
  chapter_count: number;
}): void {
  track('reel_clip_generation_failed', props);
}

export function trackReelSaved(props: {
  story_id?: string;
  clips_count: number;
  chapter_count: number;
  excluded_beats_count: number;
  total_duration: number;
  outline_title: string;
}): void {
  track('reel_saved', props);
}

// ─── Reel Editor ─────────────────────────────────────────────────
//
// PLAT-11063. Deliberately a separate family from the Reel Producer above: a
// reel a creator assembled by hand and a reel the generator produced are
// different products of different work, and one `reel_saved` carrying both
// shapes would make neither readable (DS7). Hence `reel_editor_saved` here.
//
// Two rules bind every function below. One event per *completed* action — an
// abandoned drag or a command that refused fires nothing, or the ratios these
// events exist to measure are inflated by intent (DS2). And counts, indices,
// enums and ids only: a Section's name is the creator's copy and a search query
// is transcript text typed back at us, so neither travels (DS3).

/** Where a name stood before an edit — see `NameSource` in `services/reel`. */
type ReelNameSource = 'clip' | 'generated' | 'user';

/** What set a reel's naming run going: opening a placeholder-titled reel, or an ask. */
type ReelNameRunSource = 'creation' | 'manual';

/**
 * `clips_selected` against `sections_resolved` separates what the creator
 * picked from what survived `clipFragmentsFromClips`; a gap between them is
 * clips without a source range, which is a content problem rather than an
 * editor one.
 */
export function trackReelCreatedFromClips(props: {
  story_id?: string;
  clips_selected: number;
  sections_resolved: number;
  /** The grid's multi-select bar, or the clip preview's Create Reel from Clip. */
  source: 'video_clips' | 'clip_preview';
}): void {
  track('reel_created_from_clips', props);
}

export function trackReelEditorOpened(props: {
  story_id?: string;
  clip_id?: string;
  section_count: number;
  chapter_count: number;
  disabled_section_count: number;
}): void {
  track('reel_editor_opened', props);
}

/**
 * `carried_element_count` is the epic's "a Section is a unit" promise, measured:
 * a population of moves that all carry zero elements would say the carry-over is
 * silently not firing.
 */
export function trackReelSectionMoved(props: {
  clip_id?: string;
  from_index: number;
  to_index: number;
  crossed_chapter: boolean;
  carried_element_count: number;
}): void {
  track('reel_section_moved', props);
}

export function trackReelSectionRemoved(props: {
  clip_id?: string;
  section_count_after: number;
}): void {
  track('reel_section_removed', props);
}

/**
 * `sections_added` is not always 1: replacing with a clip that is itself a reel
 * opens one Section per passage (DE15).
 */
export function trackReelSectionReplaced(props: {
  clip_id?: string;
  index: number;
  sections_added: number;
}): void {
  track('reel_section_replaced', props);
}

export function trackReelSectionToggled(props: {
  clip_id?: string;
  enabled: boolean;
  section_count_after: number;
}): void {
  track('reel_section_toggled', props);
}

/**
 * `insert_position` tells apart the two ways into the same picker: a gap says
 * the creator aimed at a specific seam (PLAT-11068), where the utility bar just
 * adds at the playhead (PLAT-11057) — composing rather than collecting.
 */
export function trackReelClipsAdded(props: {
  clip_id?: string;
  clips_added: number;
  insert_position: 'playhead' | 'gap';
  section_count_after: number;
}): void {
  track('reel_clips_added', props);
}

/**
 * `was_implicit` separates the chapter the creator declared from the one
 * PLAT-11055 DS3 opens at Section 1 on their behalf — without it, chapter counts
 * overstate deliberate use by roughly double on first use.
 */
export function trackReelChapterCreated(props: {
  clip_id?: string;
  section_count: number;
  was_implicit: boolean;
  trigger: 'divider' | 'playhead';
}): void {
  track('reel_chapter_created', props);
}

/**
 * PLAT-11099 — the Auto-Chapter run, as a Started/Completed/Failed triplet.
 *
 * One event pair for the whole action rather than one per option: chapterization
 * is one run with attributes (E1), so the options are properties here and every
 * option a later story adds is one more property rather than one more event.
 * `chapter_count` is what was *asked for* and is absent for auto — what came back
 * is on the completed event, and the gap between the two is the only measure of
 * whether the count is being honoured (DS11 leaves it unenforced by the client).
 */
export function trackReelChapterizationStarted(props: {
  clip_id?: string;
  section_count: number;
  chapter_count?: number;
  /** PLAT-11153 — a property on the same run, not an event of its own (E1). */
  reordered: boolean;
  has_guidance: boolean;
  /**
   * PLAT-11154 — equal to `sections_total` on a whole-reel run, which is what
   * makes "how often is a subset chosen" answerable from one event rather than
   * from the absence of another.
   */
  sections_selected: number;
  sections_total: number;
}): void {
  track('reel_chapterization_started', props);
}

export function trackReelChapterizationCompleted(props: {
  clip_id?: string;
  section_count: number;
  chapter_count: number;
  reordered: boolean;
  sections_selected: number;
  sections_total: number;
}): void {
  track('reel_chapterization_completed', props);
}

/**
 * No `error_message`: the reason is already logged with full context, and the
 * failures here are a model returning something unusable as often as they are a
 * transport error — a free-text property would be a high-cardinality field
 * carrying prompt output into analytics.
 */
export function trackReelChapterizationFailed(props: {
  clip_id?: string;
  section_count: number;
}): void {
  track('reel_chapterization_failed', props);
}

export function trackReelChapterMoved(props: {
  clip_id?: string;
  from_index: number;
  to_index: number;
  section_count: number;
}): void {
  track('reel_chapter_moved', props);
}

export function trackReelChapterCollapsed(props: {
  clip_id?: string;
  collapsed: boolean;
}): void {
  track('reel_chapter_collapsed', props);
}

/**
 * PLAT-11203 — one event for both directions: `hidden` says which way the toggle
 * went, and `chapter_count` how much structure was being put away — the only
 * reading that separates "this reel is over-chaptered" from "I hide them to
 * scan".
 */
export function trackReelChaptersHidden(props: {
  clip_id?: string;
  hidden: boolean;
  chapter_count: number;
}): void {
  track('reel_chapters_hidden', props);
}

/**
 * PLAT-11164 — the reel's structure inside the transcript (PLAT-11155).
 *
 * Every event carries `surface` from day one: PLAT-11172 fires the SAME names
 * with `creator_studio`, and a property added after the fact splits the
 * dataset in two. Ids and counts only — Section and Chapter names are
 * creator-authored content and are never sent.
 */
export type ReelTranscriptSurface = 'clip_preview' | 'creator_studio';
/**
 * The two levels that divide a reel. There is deliberately no reel level:
 * PLAT-11253 replaced the reel heading row with a summary line that does not
 * fold, so nothing can emit one (epic DE11).
 */
export type ReelTranscriptRowLevel = 'chapter' | 'section';
export type ReelTranscriptAutoExpandReason =
  | 'playback'
  | 'search'
  | 'jump'
  | 'mount';

/** Fires once per clip, not per render — a re-derive on edit must not repeat it. */
export function trackReelTranscriptStructureShown(props: {
  surface: ReelTranscriptSurface;
  story_id?: string;
  clip_id?: string;
  section_count: number;
  chapter_count: number;
}): void {
  track('reel_transcript_structure_shown', props);
}

export function trackReelTranscriptRowCollapsed(props: {
  surface: ReelTranscriptSurface;
  level: ReelTranscriptRowLevel;
  row_id: string;
  sections_hidden: number;
}): void {
  track('reel_transcript_row_collapsed', props);
}

export function trackReelTranscriptRowExpanded(props: {
  surface: ReelTranscriptSurface;
  level: ReelTranscriptRowLevel;
  row_id: string;
}): void {
  track('reel_transcript_row_expanded', props);
}

/** The app opened a folded row on the creator's behalf — never a manual expand. */
export function trackReelTranscriptRowAutoExpanded(props: {
  surface: ReelTranscriptSurface;
  level: ReelTranscriptRowLevel;
  row_id: string;
  reason: ReelTranscriptAutoExpandReason;
}): void {
  track('reel_transcript_row_auto_expanded', props);
}

export function trackReelTranscriptSectionSeeked(props: {
  surface: ReelTranscriptSurface;
  section_id: string;
  section_index: number;
}): void {
  track('reel_transcript_section_seeked', props);
}

export function trackReelTranscriptEditorRevealed(props: {
  surface: ReelTranscriptSurface;
  section_id: string;
}): void {
  track('reel_transcript_editor_revealed', props);
}

/**
 * PLAT-11204 — the confirmed removal only; a cancel is not an edit.
 * `chapter_count` against `reel_chapterization_completed`'s own count is how we
 * find out whether Auto-Chapter's output is being thrown away wholesale.
 */
export function trackReelChaptersRemovedAll(props: {
  clip_id?: string;
  chapter_count: number;
  section_count: number;
}): void {
  track('reel_chapters_removed_all', props);
}

/**
 * Renaming away from `'generated'` is the clearest single signal that a
 * generated name did not land.
 */
export function trackReelSectionRenamed(props: {
  clip_id?: string;
  name_source_before: ReelNameSource;
}): void {
  track('reel_section_renamed', props);
}

export function trackReelChapterRenamed(props: {
  clip_id?: string;
  title_source_before: ReelNameSource;
}): void {
  track('reel_chapter_renamed', props);
}

/**
 * PLAT-11213 — the reel's own name.
 *
 * `source` separates the run that fires when a placeholder-titled reel is opened
 * from the one a creator asks for: the first is the feature working, the second
 * is the first one's output being rejected.
 */
export function trackReelNameGenerated(props: {
  clip_id?: string;
  story_id?: string;
  source: ReelNameRunSource;
  section_count: number;
  name_length: number;
}): void {
  track('reel_name_generated', props);
}

export function trackReelNameGenerationFailed(props: {
  clip_id?: string;
  story_id?: string;
  source: ReelNameRunSource;
  error_message: string;
}): void {
  track('reel_name_generation_failed', props);
}

/**
 * `was_placeholder` rather than the `name_source_before` a Section and Chapter
 * rename carry: a reel stores no provenance (DS2), so the only knowable thing is
 * whether the name being replaced was the one every reel is created with. The
 * reading that matters — a rename away from a *generated* name — is
 * `reel_name_generated` followed by this on the same `clip_id`.
 */
export function trackReelRenamed(props: {
  clip_id?: string;
  was_placeholder: boolean;
  name_length: number;
}): void {
  track('reel_renamed', props);
}

/**
 * `query_length` rather than the query (DS3): it separates a real search from a
 * stray keystroke without carrying a word of what the creator typed.
 */
export function trackReelEditorSearched(props: {
  clip_id?: string;
  match_count: number;
  query_length: number;
}): void {
  track('reel_editor_searched', props);
}

export function trackReelEditorUndone(props: { clip_id?: string }): void {
  track('reel_editor_undone', props);
}

export function trackReelEditorRedone(props: { clip_id?: string }): void {
  track('reel_editor_redone', props);
}

/**
 * A background save completing — the only moment a reel is persisted, since the
 * Reel Editor has no Save button and every edit schedules the debounced
 * auto-save instead (DS4).
 *
 * It carries three things at once: what the save was flushing (`triggers`), what
 * it cost, and the reel's whole shape at that moment. One row per save is more
 * than a census needs, but the last row per `clip_id` *is* the final shape — so
 * "did the generated names survive" is a latest-per-reel query rather than a
 * replay of the edit stream, and every earlier row buys the latency series.
 *
 * `triggers` is a set because saves coalesce twice over — the debounce collapses
 * a burst, and a save can queue behind one already in flight. Naming a single
 * cause would be a guess about which of them mattered. `queued` is what
 * separates a slow save from one that was merely late.
 *
 * Fires for every clip-preview save, not only reel ones (DS8): the path is
 * shared, and the non-reel baseline is what makes a reel's latency number mean
 * anything. The census fields are present only when `is_reel`.
 */
export function trackReelEditorSaved(props: {
  clip_id?: string;
  is_reel: boolean;
  triggers: string[];
  trigger_count: number;
  queued: boolean;
  prepare_ms: number;
  save_ms: number;
  total_ms: number;
  section_count?: number;
  chapter_count?: number;
  user_named_count?: number;
  generated_name_count?: number;
}): void {
  track('reel_editor_saved', props);
}

// ─── Clip Link ───────────────────────────────────────────────────

export function trackClipLinkCopy(props: {
  clip_id?: string;
  story_id?: string;
  action: 'copy' | 'open';
  link_type: 'unlisted';
}): void {
  track('clip_link_copy', props);
}

// ─── Story Upload ────────────────────────────────────────────────

export function trackStoryUploadStarted(props: {
  story_id?: string;
  file_name?: string;
  file_size_mb?: number;
  file_type?: string;
  /** Where the Add Stories dialog was opened from (PLAT-11948). */
  entry_point?: string;
}): void {
  track('story_upload_started', props);
}

/** The Add Stories dialog opened (PLAT-11948). */
export function trackAddStoriesOpened(props: { entry_point: string }): void {
  track('add_stories_opened', props);
}

/**
 * The Add Stories dialog was closed before Save (PLAT-11948). An upload that
 * already started keeps running — this is not an upload failure.
 */
export function trackAddStoriesAbandoned(props: {
  entry_point: string;
  step: 'upload' | 'story_details';
  upload_started: boolean;
}): void {
  track('add_stories_abandoned', props);
}

export function trackStoryUploadCompleted(props: {
  story_id?: string;
  file_size_mb?: number;
  duration_seconds?: number;
  // TTFE additions (PLAT-11223): upload share of TTFE by file-size bucket is
  // the evidence gate for the client-demux decision — bytes + wall time +
  // throughput are captured from day one.
  file_size_bytes?: number;
  upload_wall_time_ms?: number;
  upload_throughput_bytes_per_sec?: number;
  ttfe_session_id?: string;
  ttfe_elapsed_ms?: number;
}): void {
  track('story_upload_completed', props);
}

export function trackStoryUploadFailed(props: {
  story_id?: string;
  file_size_mb?: number;
  error_message?: string;
}): void {
  track('story_upload_failed', props);
}

// ─── Client video analysis (PLAT-12104 / PLAT-12105) ─────────────
//
// Scene + face detection in the browser while the video uploads. Every run
// ends in exactly one of completed+saved, completed+failed(save_failed),
// skipped or failed; the server's detection Lambdas cover all but saved.

export function trackVideoAnalysisCompleted(props: {
  story_id: string;
  backend: 'webgpu' | 'wasm';
  gpu_vendor?: string;
  elapsed_ms: number;
  model_load_ms?: number;
  video_duration_s: number;
  realtime_factor: number;
  width: number;
  height: number;
  codec?: string;
  file_size_mb: number;
  frames_decoded: number;
  frames_sampled: number;
  faces_detected: number;
  scene_count: number;
  people_count: number;
  finished_before_upload: boolean;
}): void {
  track('video_analysis_completed', props);
}

export function trackVideoAnalysisSkipped(props: {
  story_id: string;
  reason: string;
  codec?: string;
}): void {
  track('video_analysis_skipped', props);
}

export function trackVideoAnalysisFailed(props: {
  story_id: string;
  error_code: string;
  phase: 'loading-models' | 'analyzing' | 'saving';
  elapsed_ms: number;
  error_message?: string;
}): void {
  track('video_analysis_failed', props);
}

export function trackVideoAnalysisSaved(props: {
  story_id: string;
  save_ms: number;
  payload_kb: number;
}): void {
  track('video_analysis_saved', props);
}

// ─── TTFE event spine ────────────────────────────────────────────
//
// PLAT-11223. TTFE = file-select → transcript/timeline accepts the first
// edit; time-to-canvas is the sibling metric ending at the renderer
// handshake. Sessionization, dedupe, and elapsed-time computation live in
// `TtfeTracker.ts` — these stay dumb emitters like everything else here.
// Client-observed timestamps are canonical (DatoCMS polling latency is part
// of what users experience; analyses note the ~15s quantization).

/** Clock start: a file was picked in the Add Story flow. */
export function trackTtfeFileSelected(props: {
  ttfe_session_id: string;
  file_size_bytes: number;
  file_count: number;
  file_type?: string;
}): void {
  track('ttfe.file_selected', props);
}

/**
 * A per-step readiness signal became visible to the client via the DatoCMS
 * query subscription. One event per signal per upload session.
 */
export function trackTtfeSignalObserved(props: {
  story_id: string;
  signal: string;
  ttfe_session_id: string;
  ttfe_elapsed_ms: number;
}): void {
  track('ttfe.signal_observed', props);
}

/** Clock stop: the transcript or timeline accepted the user's first edit. */
export function trackTtfeFirstEditAccepted(props: {
  story_id: string;
  edit_surface: 'transcript' | 'timeline';
  ttfe_session_id: string;
  ttfe_elapsed_ms: number;
}): void {
  track('ttfe.first_edit_accepted', props);
}

/** Second metric: the canvas became usable (renderer handshake complete). */
export function trackTtcCanvasReady(props: {
  story_id: string;
  source: '270p';
  ttfe_session_id: string;
  ttc_elapsed_ms: number;
}): void {
  track('ttc.canvas_ready', props);
}

/**
 * PLAT-11697 Audio Sidecar: the on-device demux finished for this upload
 * (`sidecar_bytes` is 0 when the Timing Gate failed and nothing was built).
 */
export function trackTtfeSidecarDemuxCompleted(props: {
  story_id?: string;
  ttfe_session_id: string;
  ttfe_elapsed_ms: number;
  demux_ms: number;
  original_bytes: number;
  sidecar_bytes: number;
  gate_pass: boolean;
}): void {
  track('ttfe.sidecar_demux_completed', props);
}

/** PLAT-11697: the Audio Sidecar landed in S3 ahead of the Original. */
export function trackTtfeSidecarUploadCompleted(props: {
  story_id?: string;
  ttfe_session_id: string;
  ttfe_elapsed_ms: number;
  wall_ms: number;
  bytes: number;
}): void {
  track('ttfe.sidecar_upload_completed', props);
}

/**
 * PLAT-11697: this upload took the Server-Extract Path. `skip_reason` is a
 * `SidecarSkipReason` (demux limitation, gate fail, or orchestration give-up).
 */
export function trackTtfeSidecarSkipped(props: {
  story_id?: string;
  ttfe_session_id: string;
  ttfe_elapsed_ms: number;
  skip_reason: string;
}): void {
  track('ttfe.sidecar_skipped', props);
}

/**
 * Fires when a story video is rejected client-side by upload validation
 * (before it reaches S3). Drives false-reject-rate monitoring for the
 * fail-closed tiers.
 */
export function trackStoryVideoRejected(props: {
  reason?: string;
  file_size_mb?: number;
  file_type?: string;
}): void {
  track('story_video_rejected', props);
}

// ─── Regeneration events ─────────────────────────────────────────

/**
 * Fires when user clicks "Regenerate" on pull quotes.
 * Old Segment name preserved: regenerate_quotes
 */
export function trackPullQuoteRegenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  revision?: string;
  selected_language?: string;
  selected_topics?: string[];
}): void {
  track('regenerate_quotes', props);
}

/**
 * Fires when user clicks "Regenerate" on blog article.
 * Old Segment name preserved: regenerate_blog
 */
export function trackArticleRegenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  revision?: string;
  selected_language?: string;
  selected_topics?: string[];
}): void {
  track('regenerate_blog', props);
}

/**
 * Fires when user clicks "Regenerate" on social post.
 * Old Segment name preserved: regenerate_social_posts
 */
export function trackSocialPostRegenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  revision?: string;
  selected_language?: string;
  selected_topics?: string[];
}): void {
  track('regenerate_social_posts', props);
}

/**
 * Fires when user clicks "Regenerate" on email.
 * Old Segment name preserved: regenerate_email
 */
export function trackEmailRegenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  revision?: string;
  selected_language?: string;
  selected_topics?: string[];
}): void {
  track('regenerate_email', props);
}

/**
 * Fires when user clicks "Regenerate" on talking points.
 * Old Segment name preserved: regenerate_talking_points
 */
export function trackTalkingPointsRegenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  revision?: string;
  selected_language?: string;
  selected_topics?: string[];
}): void {
  track('regenerate_talking_points', props);
}

// ─── Producer opened (net-new) ───────────────────────────────────

/**
 * Fires when user opens the Email producer modal/view.
 * NEW event — no Segment equivalent.
 *
 * NOTE: payload key is `action_source` (not `source`) because PostHog's
 * initializer registers `source: 'frontend'` as a super-property and
 * re-sets it in capture() — using `source` here would be silently
 * overwritten. See PLAT-9680.
 */
export function trackEmailProducerOpened(props: {
  story_id?: string;
  story_title?: string;
  action_source?: string;
}): void {
  track('email_producer_opened', props);
}

/**
 * Fires when user opens the Talking Points producer modal/view.
 * NEW event — no Segment equivalent.
 *
 * NOTE: payload key is `action_source` (not `source`) because PostHog's
 * initializer registers `source: 'frontend'` as a super-property and
 * re-sets it in capture() — using `source` here would be silently
 * overwritten. See PLAT-9680.
 */
export function trackTalkingPointsProducerOpened(props: {
  story_id?: string;
  story_title?: string;
  action_source?: string;
}): void {
  track('talking_points_producer_opened', props);
}

// ─── Email ──────────────────────────────────────────────────────

/**
 * Fires when user clicks "Generate" on email.
 * Old Segment name preserved: generate_email
 */
export function trackEmailGenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  selected_language?: string;
  goal?: string;
  audience_count?: number;
  brand_voice?: boolean;
  selected_topics?: string[];
}): void {
  track('generate_email', props);
}

/**
 * Fires when user saves an email.
 * Old Segment name preserved: save_email
 */
export function trackEmailSaved(props: {
  story_id?: string;
  story_title?: string;
}): void {
  track('save_email', props);
}

/**
 * Fires when user copies an email.
 * Old Segment name preserved: copy_email
 */
export function trackEmailCopied(props: {
  story_id?: string;
  story_title?: string;
}): void {
  track('copy_email', props);
}

// ─── Translations ───────────────────────────────────────────────

/**
 * Fires when the user submits a translation Generate or Regenerate.
 * `action` distinguishes first-generate (from the modal) vs. destructive
 * rebuild (from the toggle's Regenerate entry). Aborted attempts fire
 * Started but do NOT fire Completed or Failed — matches the store's
 * idle-on-abort treatment.
 */
export function trackTranslationStarted(props: {
  story_id?: string;
  source_lang: string;
  target_lang: string;
  handler_id?: string;
  action: 'generate' | 'regenerate';
}): void {
  track('translation_started', props);
}

/**
 * Fires when a translation Generate or Regenerate returns successfully.
 * `duration_ms` is wall-clock elapsed on the client (includes backend +
 * network); `phrase_count` is the returned phrase count for coarse size
 * slicing.
 */
export function trackTranslationCompleted(props: {
  story_id?: string;
  source_lang: string;
  target_lang: string;
  handler_id?: string;
  action: 'generate' | 'regenerate';
  duration_ms: number;
  phrase_count?: number;
}): void {
  track('translation_completed', props);
}

/**
 * Fires when a translation Generate or Regenerate throws (non-abort).
 * `error_message` mirrors the repo's `extractErrorInfo` convention.
 */
export function trackTranslationFailed(props: {
  story_id?: string;
  source_lang: string;
  target_lang: string;
  handler_id?: string;
  action: 'generate' | 'regenerate';
  error_message?: string;
}): void {
  track('translation_failed', props);
}

/**
 * Fires when the user picks an SRT file to upload as a translation.
 * `replaced_existing` distinguishes fresh-install (PLAT-9779) from
 * replace-existing (PLAT-9781) call sites; both emit the same event.
 */
export function trackTranslationSrtUploadStarted(props: {
  story_id?: string;
  language: string;
  filename: string;
  size_bytes: number;
  replaced_existing: boolean;
}): void {
  track('translation_srt_upload_started', props);
}

/**
 * Fires when the SRT upload returns a usable translation. `coverage_percent`
 * is text-only (punct elements inherit from neighbors); `uncovered_count`
 * is the size of the uncovered indices list returned by the server.
 */
export function trackTranslationSrtUploadCompleted(props: {
  story_id?: string;
  language: string;
  filename: string;
  duration_ms: number;
  phrase_count: number;
  coverage_percent: number;
  uncovered_count: number;
  replaced_existing: boolean;
}): void {
  track('translation_srt_upload_completed', props);
}

/**
 * Fires when the SRT upload fails (non-abort). `error_code` is the
 * server's response `code` (`SRT_PARSE_ERROR`, `EMPTY_SRT_BODY`, etc.)
 * when present; `error_message` is the user-facing message.
 */
export function trackTranslationSrtUploadFailed(props: {
  story_id?: string;
  language: string;
  filename: string;
  error_message?: string;
  error_code?: string;
  replaced_existing: boolean;
}): void {
  track('translation_srt_upload_failed', props);
}

/**
 * Fires when the reviewer confirms a whole-script directed re-translate from
 * the DirectedRetranslateModal (PLAT-10258). `mode` distinguishes the
 * fix-handling path — `fresh` discards existing manual fixes, `keep` re-applies
 * them onto the rebased script; `instruction_length` is the free-text steer
 * length; `fix_count` is the number of live manual fixes at confirm time.
 */
export function trackTranslationDirectedRetranslate(props: {
  mode: 'fresh' | 'keep';
  instruction_length: number;
  fix_count: number;
}): void {
  track('translation_directed_retranslate', props);
}

// ─── Composition translation (PLAT-9870 / PLAT-9967) ────────────────

/**
 * Fires alongside the transcript `translation_started` event when the
 * bulk-translate flow has composition targets to send to the backend.
 * `target_count` is the per-placement target count after preset
 * short-circuit, `unique_phrases` is the deduped sourceText set (what
 * actually hits the LLM), `preset_skipped` counts targets covered by a
 * template preset and never sent. `from_bulk` distinguishes this event's
 * origin from per-composition retranslate (PLAT-9968) and translate-on-
 * save (PLAT-9969).
 */
export function trackCompositionTranslationStarted(props: {
  story_id?: string;
  language: string;
  target_count: number;
  unique_phrases: number;
  preset_skipped: number;
  from_bulk: boolean;
}): void {
  track('composition_translation_started', props);
}

/**
 * Fires when the composition pipeline returns `status: 'ok'` from the
 * backend. Independent of the transcript completion event since the two
 * pipelines can succeed or fail independently. `phrases_added`,
 * `phrases_skipped_pinned`, and `phrases_orphaned` are the backend's
 * post-merge counts.
 */
export function trackCompositionTranslationCompleted(props: {
  story_id?: string;
  language: string;
  duration_ms: number;
  target_count: number;
  unique_phrases: number;
  preset_skipped: number;
  phrases_added: number;
  phrases_skipped_pinned: number;
  phrases_orphaned: number;
  from_bulk: boolean;
}): void {
  track('composition_translation_completed', props);
}

/**
 * Fires when the composition pipeline returns `status: 'error'` from the
 * backend, OR when the whole request rejects (in which case the transcript
 * pipeline also failed). `error_code` is the backend's per-section error
 * code (`PROVIDER_ERROR`, `MISSING_CREDENTIALS`, etc.) when available.
 */
export function trackCompositionTranslationFailed(props: {
  story_id?: string;
  language: string;
  error_message: string;
  error_code?: string;
  from_bulk: boolean;
}): void {
  track('composition_translation_failed', props);
}

/**
 * PLAT-9968 — surface tag for the composition quick-edit popover. Add a
 * new value here when a sibling surface mounts the popover. PLAT-10120
 * adds `'creator-studio-transcript'` (the Creator Studio left transcript's
 * overlay-underline hover pill); PLAT-10211 adds `'creator-studio-canvas'`
 * (the Creator Studio stage action menu, opened by a canvas click or — via
 * PLAT-10122 — a timeline selection).
 */
export type CompositionQuickEditSurface =
  | 'content-studio-clip-preview'
  | 'creator-studio-transcript'
  | 'creator-studio-canvas';

/**
 * PLAT-10046 — composition placement kind, shared across every
 * composition-scope tracker payload so a new composition variety only
 * needs one update site. Mirrors the literal union the popover
 * surfaces (`CompositionQuickEditPopoverProps.compositionType`).
 */
export type CompositionTypeTag =
  | 'introCard'
  | 'endCard'
  | 'nameCard'
  | 'overlay';

/**
 * Fires when the user opens the composition quick-edit popover by
 * clicking an intro / end / nametag chip in the clip-preview transcript.
 * `field_count` is the number of editable rows displayed; `has_translation`
 * is true when at least one row has a translated value to show alongside
 * the original.
 */
export function trackCompositionQuickEditOpened(props: {
  story_id?: string;
  composition_element_id: string;
  composition_type: CompositionTypeTag;
  surface: CompositionQuickEditSurface;
  field_count: number;
  has_translation: boolean;
  active_language: string;
}): void {
  track('composition_quick_edit_opened', props);
}

/**
 * Fires when the user commits an original-text edit via the popover's
 * Save button. Original-language edits propagate through
 * `editor.commandUpdateElement` so the canvas updates in place and the
 * change participates in undo/redo.
 *
 * PLAT-10066 — `language` records the active caption language at the
 * moment of save. `'<original>'` when the user is on the source language;
 * a BCP 47 code (e.g. `'es'`) when an original-text edit happens while
 * the popover is mounted under a non-original caption language (the
 * quick-edit-only / non-translatable row case — translated-value pins
 * fire `trackCompositionPhraseEdited` instead). Lets dashboards split
 * original-text edits by language without joining against a session
 * dimension.
 */
export function trackCompositionTextEdited(props: {
  story_id?: string;
  composition_element_id: string;
  composition_type: CompositionTypeTag;
  surface: CompositionQuickEditSurface;
  language: string;
  source_text_length: number;
}): void {
  track('composition_text_edited', props);
}

/**
 * PLAT-10046 — fires when the user commits a manual translated-value pin
 * via the popover's Save. Distinct from `composition_text_edited` (original-
 * language edit) so dashboards can split translation pins from original-
 * text edits. `source_text_length` keeps the payload bounded — no
 * plaintext sourceText or translatedValue in the body.
 */
export function trackCompositionPhraseEdited(props: {
  story_id?: string;
  composition_element_id: string;
  composition_type: CompositionTypeTag;
  surface: CompositionQuickEditSurface;
  language: string;
  source_text_length: number;
}): void {
  track('composition_phrase_edited', props);
}

/**
 * PLAT-10046 — fires when the user confirms the "Translate composition"
 * footer button (composition has at least one uncovered translatable row
 * under a non-original caption language). The dispatch path is the same
 * `TranslationStore.retranslateCompositionPhrases` call PLAT-9969 will
 * fire automatically; this tracker records the human-initiated motion.
 */
export function trackCompositionTranslateClicked(props: {
  story_id?: string;
  composition_element_id: string;
  composition_type: CompositionTypeTag;
  surface: CompositionQuickEditSurface;
  language: string;
  source_texts_count: number;
  references_affected: number;
  other_compositions_affected: number;
}): void {
  track('composition_translate_clicked', props);
}

/**
 * PLAT-10046 — fires when the user confirms the "Retranslate composition"
 * footer button (every translatable row already resolves to a story phrase
 * and the user wants to discard those AI translations + re-run the LLM
 * with `force: true`). Preset-locked rows are unaffected regardless of
 * force; pinned manual edits matching the affected sourceTexts are cleared.
 */
export function trackCompositionRetranslateClicked(props: {
  story_id?: string;
  composition_element_id: string;
  composition_type: CompositionTypeTag;
  surface: CompositionQuickEditSurface;
  language: string;
  source_texts_count: number;
  references_affected: number;
  other_compositions_affected: number;
}): void {
  track('composition_retranslate_clicked', props);
}

/**
 * PLAT-10046 — fires when the per-composition translate/retranslate call
 * fails. Surface (clip-preview today, more later) + language + action
 * differentiate Translate vs Retranslate failures in the funnel.
 * `error_message` is the formatted message from `extractErrorInfo`; no
 * stack traces, no raw response bodies — same convention as other
 * translation-error events.
 */
export function trackCompositionTranslateFailed(props: {
  story_id?: string;
  composition_element_id: string;
  composition_type: CompositionTypeTag;
  surface: CompositionQuickEditSurface;
  language: string;
  action: 'translate' | 'retranslate';
  error_message: string;
}): void {
  track('composition_translate_failed', props);
}

// ─── Add overlay graphic from transcript (PLAT-10105) ───────────────

/**
 * PLAT-10105 — fires when the user confirms an overlay graphic in the
 * picker opened from a clip-preview transcript selection (insert begins).
 * `time_start` / `time_end` are the resolved timeline timestamps of the
 * highlighted range the overlay is bound to.
 */
export function trackOverlayAddedFromTranscriptStarted(props: {
  story_id?: string;
  surface: CompositionQuickEditSurface;
  time_start: number;
  time_end: number;
}): void {
  track('overlay_added_from_transcript_started', props);
}

/**
 * PLAT-10105 — fires when the overlay composition is successfully inserted
 * onto the clip timeline. `composition_id` is the source template id; the
 * resolved range is carried for funnel/range analysis. BEHAVIORAL.
 */
export function trackOverlayAddedFromTranscriptCompleted(props: {
  story_id?: string;
  surface: CompositionQuickEditSurface;
  composition_id: string;
  time_start: number;
  time_end: number;
}): void {
  track('overlay_added_from_transcript_completed', props);
}

/**
 * PLAT-10105 — fires when the overlay insert fails. Paired with a
 * user-facing error (no silent failure). `error_message` is the formatted
 * message from `extractErrorInfo`.
 */
export function trackOverlayAddedFromTranscriptFailed(props: {
  story_id?: string;
  surface: CompositionQuickEditSurface;
  composition_id: string;
  error_message: string;
}): void {
  track('overlay_added_from_transcript_failed', props);
}

// ─── Smart Graphics AI text control (PLAT-10923) ─────────────────────

/**
 * PLAT-10923 — every surface an AI text control renders on.
 *
 * A superset of `CompositionQuickEditSurface` rather than that union widened:
 * the control also renders in the Creator Studio composition settings panel
 * (PLAT-10920) and the video template's hook slot (PLAT-11034), neither of
 * which mounts the quick-edit popover. Widening the popover's own union would
 * make a settings-panel value passable to `trackCompositionQuickEditOpened`,
 * where it would mean nothing.
 */
export type AITextControlSurface =
  | CompositionQuickEditSurface
  | 'creator-studio-settings-panel'
  | 'creator-studio-video-template';

/**
 * Which words the generation read (epic E4). Mirrors `AITextBaseTextScope`,
 * declared here so the analytics layer has no import into the control module.
 */
export type AITextBaseTextTag = 'clip' | 'selection';

/**
 * PLAT-10923 — who fired, on what. Carried by every AI text event, so one
 * graphic's whole lifecycle joins on `composition_element_id` while dashboards
 * split by surface or by the template driving the control.
 *
 * `composition_element_id` is the timeline element (the join key, always
 * present); `composition_id` is the DatoCMS composition template it came from.
 * Both, because the epic seeds two hook cards that differ only in placement and
 * base-text scope — without the template id they are indistinguishable in the
 * data, and without the element id two graphics on one clip are.
 *
 * A type alias rather than an `interface`, deliberately: only aliases get the
 * implicit index signature that lets an intersection of them satisfy `track`'s
 * `Record<string, unknown>` parameter.
 */
export type GraphicsAITextIdentity = {
  story_id?: string;
  surface: AITextControlSurface;
  composition_element_id: string;
  composition_id?: string;
  control_id: string;
};

/**
 * Fires immediately before the generation request goes out — automatic
 * (`trigger: 'insert'`, fired when the composition is added, epic E12) or
 * explicit (`trigger: 'manual'`, the control's Regenerate). `is_rerun` is true
 * when the control already had a generation record, `has_instructions` when the
 * creator supplied a steer.
 *
 * Started / Completed / Failed is a triplet: a Started with neither sibling is
 * how a hung generation is visible at all. See
 * `trackGraphicsAiTextGenerationCompleted` for the one deliberate gap.
 */
export function trackGraphicsAiTextGenerationStarted(
  props: GraphicsAITextIdentity & {
    base_text: AITextBaseTextTag;
    trigger: 'insert' | 'manual';
    is_rerun: boolean;
    has_instructions: boolean;
  },
): void {
  track('graphics_ai_text_generation_started', props);
}

/**
 * Fires when the preferred option has been written to the element.
 * `character_count` is that option's length and `option_count` the size of the
 * returned set — never the copy itself, which is customer content.
 *
 * **The documented triplet gap:** when two runs race for one control, the loser
 * is discarded before it writes (PLAT-10917 DS5), so it fires Started and
 * neither Completed nor Failed. That is intentional — it failed at nothing and
 * completed nothing — so a small Started surplus is expected rather than a sign
 * of hung generations.
 */
export function trackGraphicsAiTextGenerationCompleted(
  props: GraphicsAITextIdentity & {
    base_text: AITextBaseTextTag;
    character_count: number;
    option_count: number;
    duration_ms: number;
  },
): void {
  track('graphics_ai_text_generation_completed', props);
}

/**
 * Fires on every failing exit path — the model call, an empty option set, a
 * graphic deleted mid-flight, or a throw from the write-back. `error_message`
 * is the formatted message from `extractErrorInfo` (or the service's own
 * `ServiceError` message); no stack traces, no response bodies.
 */
export function trackGraphicsAiTextGenerationFailed(
  props: GraphicsAITextIdentity & {
    base_text: AITextBaseTextTag;
    error_message: string;
  },
): void {
  track('graphics_ai_text_generation_failed', props);
}

/**
 * Fires when one of the already-generated alternatives is applied to the
 * element — a local write, no model call (epic E9). `option_index` is the index
 * into the stored set, `character_count` the applied option's length.
 */
export function trackGraphicsAiTextOptionSelected(
  props: GraphicsAITextIdentity & {
    base_text: AITextBaseTextTag;
    option_index: number;
    character_count: number;
  },
): void {
  track('graphics_ai_text_option_selected', props);
}

/**
 * Fires when the creator switches which words the next generation will read.
 * A change only — re-picking the selected scope is not one. Carries `from` and
 * `to` instead of the siblings' single `base_text`, since the whole event is
 * the transition.
 */
export function trackGraphicsAiTextBaseTextChanged(
  props: GraphicsAITextIdentity & {
    from: AITextBaseTextTag;
    to: AITextBaseTextTag;
  },
): void {
  track('graphics_ai_text_base_text_changed', props);
}

/**
 * Fires when the "Transcript changed" indicator appears for a control — once
 * per transition into stale, not once per render (the indicator re-renders
 * constantly, and a render counter would be a meaningless metric). `base_text`
 * is the scope the stale generation read, which is what the compare re-resolves.
 */
export function trackGraphicsAiTextStaleShown(
  props: GraphicsAITextIdentity & {
    base_text: AITextBaseTextTag;
  },
): void {
  track('graphics_ai_text_stale_shown', props);
}

/**
 * PLAT-10923 — fires when the creator changes a composition's **timeline** pin
 * override (epic E7). Not an AI event: it carries no `control_id` and no
 * `base_text`, which would be meaningless for it, and it ships here only
 * because one tracker file is cheaper than a second analytics ticket.
 *
 * `effective_pinned` is the resolved state after the change
 * (`placementPinOverride ?? placementPinned`), so `action: 'revert'` is
 * readable without knowing the template's authored value.
 *
 * Today's only call site is the settings panel's end-card pin switch, which
 * cannot produce `'revert'`. PLAT-10922 brings the three-action control (and
 * with it `'revert'`) to quick edit; a count of edges snapped back by the
 * re-evaluation belongs with it, since nothing computes one yet.
 */
export function trackGraphicPlacementPinChanged(props: {
  story_id?: string;
  surface: AITextControlSurface;
  composition_element_id: string;
  composition_id?: string;
  action: 'set_pinned' | 'set_unpinned' | 'revert';
  placement?: string;
  effective_pinned: boolean;
}): void {
  track('graphic_placement_pin_changed', props);
}

/**
 * Fires when the user flips a translated-transcript view on or off — i.e.
 * picks a language from the toggle, returns to Original, or auto-selects
 * a language from a successful Generate. `language` holds the language
 * affected (the new one on 'on', the previous one on 'off'). Today only
 * the transcript view has a toggle call-site (`view: 'transcript'`); the
 * `'speakers'` value stays in the type for Creator Studio wire-up.
 */
export function trackTranslationToggled(props: {
  story_id?: string;
  language: string;
  action: 'on' | 'off';
  view: 'transcript' | 'speakers';
}): void {
  track('translation_toggled', props);
}

/**
 * Fires when the user commits a manual phrase edit via the popover (Save).
 * Cancel does not fire; Revert has its own event (`trackTranslationReverted`).
 */
export function trackTranslationWordEdited(props: {
  story_id?: string;
  language: string;
}): void {
  track('translation_word_edited', props);
}

/**
 * Fires when the user clicks Revert in the edit popover — restores the
 * phrase to its captured machine-original translation and drops any
 * pending edit. Distinct from `trackTranslationWordEdited` so product
 * can slice "reverted" vs. "manually edited" separately.
 */
export function trackTranslationReverted(props: {
  story_id?: string;
  language: string;
}): void {
  track('translation_reverted', props);
}

/**
 * PLAT-10214 (T6): fires when a reviewer replaces a contiguous span of
 * translated words with one correction. `wordCount` is the number of source
 * words covered; `fragmentCount` the number of fragments the override produced
 * (1 when inserted as a single fragment, >1 when fine-split via the T9
 * splitter); `createdOverrideSegment` is `false` when the span collapsed to a
 * single-phrase edit (delegated to `editTranslatedPhrase`) and `true` when a
 * reversible override segment was authored. Single-word edits keep
 * `trackTranslationWordEdited`.
 */
export function trackTranslationReview_SpanReplaced(props: {
  story_id?: string;
  language: string;
  word_count: number;
  fragment_count: number;
  created_override_segment: boolean;
}): void {
  track('translation_review_span_replaced', props);
}

/**
 * PLAT-11650: a reviewer committed an in-place edit of ONE translated phrase
 * (clicked the phrase, typed, pressed Enter). `kind` is `remove` when the edit
 * only took words away, `replace` otherwise; the counts are whitespace-split
 * words before and after. Cancels fire nothing. The store's own
 * `trackTranslationWordEdited` still fires alongside (existing funnels).
 */
export function trackTranslationReview_InlineEditCommitted(props: {
  story_id?: string;
  language: string;
  kind: 'replace' | 'remove';
  words_before: number;
  words_after: number;
}): void {
  track('translation_review_inline_edit_committed', props);
}

/**
 * PLAT-10257: fires when a reviewer submits a directed span re-translate —
 * i.e. the request actually goes to the machine (pre-submit refusals such as
 * an overlapping span fire nothing, matching the manual span-replace's
 * untracked refusals). `scope` is `'span'` (the whole-script directed
 * re-translate keeps its own `trackTranslationDirectedRetranslate`);
 * `instruction_length` is the TRIMMED steer length — length only, never the
 * text. Every submit is followed by exactly one Completed or Failed.
 */
export function trackTranslationReview_DirectedRetranslate(props: {
  story_id?: string;
  language: string;
  scope: 'span';
  instruction_length: number;
}): void {
  track('translation_review_directed_retranslate', props);
}

/**
 * Fires when the directed span re-translate lands as a reversible override
 * segment (same payload family as the submit event).
 */
export function trackTranslationReview_DirectedRetranslateCompleted(props: {
  story_id?: string;
  language: string;
  scope: 'span';
  instruction_length: number;
}): void {
  track('translation_review_directed_retranslate_completed', props);
}

/**
 * Fires when a submitted directed span re-translate fails — nothing was
 * authored or persisted. `reason` is the coarse category from the hook's
 * discriminated result: `backend-failed` (the re-translate call threw) or
 * `author-failed` (the override could not be authored).
 */
export function trackTranslationReview_DirectedRetranslateFailed(props: {
  story_id?: string;
  language: string;
  scope: 'span';
  instruction_length: number;
  reason: 'backend-failed' | 'author-failed';
}): void {
  track('translation_review_directed_retranslate_failed', props);
}

/**
 * PLAT-11037: fires when a reviewer copies the shareable Translation Review
 * link from the workspace header. `entry` distinguishes the two review entry
 * points (the whole-script Content Studio review vs. the clip-scoped one), and
 * `view_mode` is the granularity the copied link reproduces.
 */
export function trackTranslationReview_LinkCopied(props: {
  story_id?: string;
  language: string;
  entry: 'story' | 'clip';
  view_mode: 'paragraph' | 'segment' | 'fragment';
}): void {
  track('translation_review_link_copied', props);
}

/**
 * PLAT-11649: the reviewer switched the workspace between its two layouts —
 * Translation Review (three panels) and Dubbing Review (translation beside a
 * large captioned player, side rails collapsed). User-initiated switches only;
 * the default the workspace opens on is not an event.
 */
export function trackTranslationReview_ModeChanged(props: {
  story_id?: string;
  language: string;
  entry: 'story' | 'clip';
  mode: 'translation' | 'dubbing';
}): void {
  track('translation_review_mode_changed', props);
}

/**
 * PLAT-11649: the reviewer collapsed or expanded a side pane in Dubbing
 * Review. Only the right (Changes / Translation Memory / Summary / Speakers)
 * pane collapses in place today; the original column's rail switches mode
 * instead and is reported by `trackTranslationReview_ModeChanged`.
 */
export function trackTranslationReview_PaneToggled(props: {
  story_id?: string;
  language: string;
  entry: 'story' | 'clip';
  pane: 'right';
  collapsed: boolean;
}): void {
  track('translation_review_pane_toggled', props);
}

/**
 * The reviewer left the Translation Review. `method` is which affordance took
 * them out — the labelled Back, an ancestor crumb, the dialog's X or Escape —
 * so product can see whether the named ways out are the ones used. Fired once
 * per exit, from the workspace; host-initiated unmounts are not exits.
 */
export function trackTranslationReview_Exited(props: {
  story_id?: string;
  language: string;
  entry: 'story' | 'clip';
  method: 'back' | 'breadcrumb' | 'close' | 'escape';
  view_mode: 'paragraph' | 'segment' | 'fragment';
}): void {
  track('translation_review_exited', props);
}

/**
 * Where a translation-removal action originated from. Single source of
 * truth for both analytics and the `TranslationToggle` `surface` prop.
 */
export type TranslationRemovalSurface =
  | 'creator-studio-transcript'
  | 'creator-studio-captions'
  | 'content-studio-transcript'
  | 'content-studio-captions'
  | 'content-studio-clip-preview';

/**
 * PLAT-9700: fires when the user confirms removal of a translation. The
 * server has already archived + deleted by the time this fires (or
 * returned 404, treated as success). `surface` lets product slice
 * removals by where they originate from — Creator Studio sidepanel /
 * captions, Content Studio transcript / captions / clip preview.
 */
export function trackTranslationLanguageRemoved(props: {
  story_id?: string;
  language: string;
  surface: TranslationRemovalSurface;
}): void {
  track('translation_language_removed', props);
}

/**
 * PLAT-9626: fires when Content Studio's Captions tab finishes mounting
 * with a translation language selected and at least one karaoke line
 * present in that language. `clip_count` is the karaoke-line count for
 * the selected language at the moment the view settles — coarse proxy
 * for "how much translated caption content the user saw". Re-fires when
 * the language selection changes; does NOT fire for the Original
 * (untranslated) selection.
 */
export function trackTranslationCaptionsViewed(props: {
  story_id?: string;
  language: string;
  clip_count: number;
}): void {
  track('translation_captions_viewed', props);
}

// ─── Talking Points ─────────────────────────────────────────────

/**
 * Fires when user clicks "Generate" on talking points.
 * Old Segment name preserved: generate_talking_points
 */
export function trackTalkingPointsGenerationStarted(props: {
  story_id?: string;
  story_title?: string;
  template?: string;
  custom_instructions?: string;
  selected_language?: string;
  selected_topics?: string[];
}): void {
  track('generate_talking_points', props);
}

/**
 * Fires when user copies talking points.
 * Old Segment name preserved: copy_talking_points
 */
export function trackTalkingPointsCopied(props: {
  story_id?: string;
  story_title?: string;
  content_length?: number;
}): void {
  track('copy_talking_points', props);
}

// ─── Settings - Distribution Channels ───────────────────────────

export function trackSettingsDistributionChannelsViewed(props: {
  enabled_count: number;
}): void {
  track('settings_distribution_channels_viewed', props);
}

export function trackDistributionChannelDetailViewed(): void {
  track('distribution_channel_detail_viewed', {});
}

export function trackSettingsDistributionChannelFiltered(props: {
  selected_channel_count: number;
  total_channel_count: number;
  selected_channel_slugs: string[];
}): void {
  track('settings_distribution_channel_filtered', props);
}

export function trackSettingsDistributionChannelViewModeChanged(props: {
  view_mode: 'list' | 'comparison';
}): void {
  track('settings_distribution_channel_view_mode_changed', props);
}

export function trackSettingsDistributionChannelDensityChanged(props: {
  density: 'summary' | 'details';
}): void {
  track('settings_distribution_channel_density_changed', props);
}

// ─── Settings - AI Voices ───────────────────────────────────────

export function trackAiVoiceActivationSaved(props: {
  dubbing_enabled: boolean;
  activated_voice_count: number;
}): void {
  track('ai_voice_activation_saved', props);
}

export function trackAiVoiceCustomPhraseAuditionStarted(props: {
  ai_voice_id: string;
  provider: string;
  language: string;
  text_length: number;
}): void {
  track('ai_voice_custom_phrase_audition_started', props);
}

export function trackAiVoiceCustomPhraseAuditionCompleted(props: {
  ai_voice_id: string;
  provider: string;
  language: string;
  text_length: number;
  duration_ms: number;
}): void {
  track('ai_voice_custom_phrase_audition_completed', props);
}

export function trackAiVoiceCustomPhraseAuditionFailed(props: {
  ai_voice_id: string;
  provider: string;
  language: string;
  error_message: string;
}): void {
  track('ai_voice_custom_phrase_audition_failed', props);
}

export function trackAiVoiceSampleListenStarted(props: {
  ai_voice_id: string;
  provider: string;
  language: string;
}): void {
  track('ai_voice_sample_listen_started', props);
}

export function trackAiVoiceSampleListenCompleted(props: {
  ai_voice_id: string;
  provider: string;
  language: string;
  duration_ms: number;
}): void {
  track('ai_voice_sample_listen_completed', props);
}

export function trackAiVoiceSampleListenFailed(props: {
  ai_voice_id: string;
  provider: string;
  language: string;
  error_message: string;
}): void {
  track('ai_voice_sample_listen_failed', props);
}

export function trackAiVoiceAuditionHistoryOpened(props: {
  count: number;
}): void {
  track('ai_voice_audition_history_opened', props);
}

export function trackAiVoiceAuditionReplayed(props: {
  ai_voice_id: string;
  provider: string;
  language: string;
}): void {
  track('ai_voice_audition_replayed', props);
}

// ─── Speakers - AI Voices (per-speaker mapping, PLAT-10501) ──────

export function trackSpeakerAiVoiceAdded(props: {
  speaker_id: string;
  ai_voice_id: string;
  voice_count: number;
}): void {
  track('speaker_ai_voice_added', props);
}

export function trackSpeakerAiVoiceRemoved(props: {
  speaker_id: string;
  ai_voice_id: string;
  voice_count: number;
}): void {
  track('speaker_ai_voice_removed', props);
}

export function trackSpeakerAiVoiceLanguageDefaultSet(props: {
  speaker_id: string;
  language_id: string;
  // null when the per-language pin is cleared (falls back to the top voice).
  ai_voice_id: string | null;
}): void {
  track('speaker_ai_voice_language_default_set', props);
}

// ─── Dubbing - Generate (PLAT-10596) ─────────────────────────────

export function trackDubGenerateStarted(props: {
  story_id?: string;
  language: string;
  speaker_count: number;
  // true for a Redub (wholesale re-synthesis), false for a first-time dub.
  regenerate: boolean;
  // 'language' = the whole saved translation (default); 'span' = a selected
  // section re-dubbed from the right-click menu (PLAT-10616); 'clip-gaps' = the
  // lines a clip is missing, filled additively from the clip preview;
  // 'clip-first' = one clip's lines dubbed in a language the story has no dub in
  // at all, started from the clip preview (PLAT-11440). Omitted ⇒ language.
  scope?: 'language' | 'span' | 'clip-gaps' | 'clip-first';
  // For a scoped run: how many period-level segments it names.
  segment_count?: number;
}): void {
  track('dub_generate_started', props);
}

export function trackDubGenerateCompleted(props: {
  story_id?: string;
  language: string;
  run_id: string;
  synthesized_count: number;
  reused_count: number;
  skipped_count: number;
  failed_count: number;
}): void {
  track('dub_generate_completed', props);
}

export function trackDubGenerateFailed(props: {
  story_id?: string;
  language: string;
  error_message: string;
}): void {
  track('dub_generate_failed', props);
}

// ─── Translation Review - Auto Re-dub (PLAT-11678) ───────────────

/**
 * PLAT-11678: fires when a saved translation edit in Translation Review starts
 * an automatic scoped re-dub. `segment_count` is how many lines go to the
 * engine; `unchanged_count` how many pending lines were left alone because
 * their recording already matched the saved text (a revert, a no-op save).
 * Every Triggered is followed by exactly one Completed or Failed.
 */
export function trackTranslationReview_AutoRedubTriggered(props: {
  story_id?: string;
  language: string;
  segment_count: number;
  unchanged_count: number;
}): void {
  track('translation_review_auto_redub_triggered', props);
}

export function trackTranslationReview_AutoRedubCompleted(props: {
  story_id?: string;
  language: string;
  run_id: string;
  synthesized_count: number;
  reused_count: number;
  failed_count: number;
}): void {
  track('translation_review_auto_redub_completed', props);
}

/**
 * PLAT-12034 — a delivery-marker chip inside a dubbed line, on the Dubbing tab
 * of Translation Review. Opened: its menu was shown. Removed: the line is
 * re-dubbed without that marker (`markers_left` says how many remain). Edit
 * requested: the line's markers editor was opened from the chip.
 */
export function trackTranslationReview_MarkerChipOpened(props: {
  story_id?: string;
  language: string;
  tag: string;
}): void {
  track('translation_review_marker_chip_opened', props);
}

export function trackTranslationReview_MarkerChipRemoved(props: {
  story_id?: string;
  language: string;
  tag: string;
  markers_left: number;
}): void {
  track('translation_review_marker_chip_removed', props);
}

export function trackTranslationReview_MarkerChipEditRequested(props: {
  story_id?: string;
  language: string;
  tag: string;
}): void {
  track('translation_review_marker_chip_edit_requested', props);
}

export function trackTranslationReview_AutoRedubFailed(props: {
  story_id?: string;
  language: string;
  segment_count: number;
  error_message: string;
}): void {
  track('translation_review_auto_redub_failed', props);
}

// ─── Translation Review - Captions (PLAT-11683) ──────────────────

/**
 * PLAT-11683: fires when the reviewer changes the Captions picker in the
 * review player header. `entry` says which review entry. Default-on at open
 * fires nothing. The review never writes captions (PLAT-11702), so there is
 * no write flag.
 */
export function trackTranslationReview_CaptionsModeChanged(props: {
  story_id?: string;
  language: string;
  mode: 'on' | 'off';
  entry: 'story' | 'clip';
  /**
   * PLAT-11702 follow-up — the showcase caption template the reviewer picked
   * from the dropdown, when the pick is what changed. Absent for a plain
   * On / Off.
   */
  template_id?: string;
}): void {
  track('translation_review_captions_mode_changed', props);
}

// ─── Dubbing - Remove (PLAT-10617) ───────────────────────────────

/**
 * PLAT-10617: fires when the user confirms removing a dub from the language
 * drop-down. The backend soft-delete (PLAT-10591) has already tombstoned the
 * segments by the time this fires (or returned 404, treated as success —
 * already gone). Mirrors `trackTranslationLanguageRemoved`.
 */
export function trackDubRemoved(props: {
  story_id?: string;
  language: string;
}): void {
  track('dub_removed', props);
}

export function trackDubRemoveFailed(props: {
  story_id?: string;
  language: string;
  error_message: string;
}): void {
  track('dub_remove_failed', props);
}

// ─── Dubbing - Translation Review (PLAT-10600) ───────────────────

/**
 * PLAT-10600: fires when the reviewer flips the Original / Dubbed / Both audio
 * toggle in the Translation Review (Story) screen — the transient A-B
 * monitoring control (`both` = the hear-both overlay). Captures overlay usage.
 */
export function trackDubReviewModeToggled(props: {
  story_id?: string;
  language: string;
  mode: 'original' | 'dubbed' | 'both';
}): void {
  track('dub_review_mode_toggled', props);
}

// ─── Dubbing - Voice/Music Separation (PLAT-10712) ───────────────
//
// Separation is language-agnostic (one decomposition per video), so the
// lifecycle events carry no `language`; the toggle/download events do (they act
// under a specific dub language). `provider` is passed through as data only —
// the frontend never branches on it (provider-agnostic; the default splitter is
// server-chosen). Started/failed report the REQUESTED provider (or 'default'
// when unset); completed reports the ACTUAL provider the backend used.

/** Separate / Reprocess kicked off. */
export function trackMusicSeparationStarted(props: {
  story_id?: string;
  provider: string;
  regenerate: boolean;
}): void {
  track('music_separation_started', props);
}

/** Separation succeeded. */
export function trackMusicSeparationCompleted(props: {
  story_id?: string;
  provider: string;
  variant_id: string;
  no_music: boolean;
  cached: boolean;
}): void {
  track('music_separation_completed', props);
}

/** Separation failed (non-abort). */
export function trackMusicSeparationFailed(props: {
  story_id?: string;
  provider: string;
  error_message: string;
}): void {
  track('music_separation_failed', props);
}

/** The None ↔ From Original music toggle changed. */
export function trackMusicModeToggled(props: {
  story_id?: string;
  mode: 'none' | 'original';
  language: string;
}): void {
  track('music_mode_toggled', props);
}

/** A separated/dubbed track was downloaded from the Music and Sounds kebab. */
export function trackMusicTrackDownloaded(props: {
  story_id?: string;
  track_type: 'voice' | 'background_music' | 'ai_dub' | 'original';
  provider: string;
  language: string;
}): void {
  track('music_track_downloaded', props);
}

/** The launch-period splitter picker selected a provider. */
export function trackMusicSplitterSelected(props: {
  story_id?: string;
  provider: string;
}): void {
  track('music_splitter_selected', props);
}

type ChannelEventProps = {
  channel_id: string;
  channel_slug: string;
  channel_title: string;
};

export function trackDistributionChannelEnableStarted(
  props: ChannelEventProps & { available_count_before: number },
): void {
  track('distribution_channel_enable_started', props);
}

export function trackDistributionChannelEnableCompleted(
  props: ChannelEventProps & { available_count_before: number },
): void {
  track('distribution_channel_enable_completed', props);
}

export function trackDistributionChannelEnableFailed(
  props: ChannelEventProps & { error_message: string },
): void {
  track('distribution_channel_enable_failed', props);
}

export function trackDistributionChannelDisableStarted(
  props: ChannelEventProps & { enabled_count_before: number },
): void {
  track('distribution_channel_disable_started', props);
}

export function trackDistributionChannelDisableCompleted(
  props: ChannelEventProps & { enabled_count_before: number },
): void {
  track('distribution_channel_disable_completed', props);
}

export function trackDistributionChannelDisableFailed(
  props: ChannelEventProps & { error_message: string },
): void {
  track('distribution_channel_disable_failed', props);
}

// ─── Home Quick Actions ──────────────────────────────────────────

export type HomeQuickAction =
  | 'generate-clips'
  | 'find-quotes'
  | 'create-article'
  | 'social-posts'
  | 'create-email'
  | 'edit-video'
  | 'add-story';

export function trackHomeQuickActionClicked(props: {
  action: HomeQuickAction;
  is_feature_flagged: boolean;
}): void {
  track('home_quick_action_clicked', props);
}

export function trackHomeQuickActionConfirmed(props: {
  action: HomeQuickAction;
  story_id: string;
  story_title?: string;
  destination_tab?: string;
  destination_route: string;
}): void {
  track('home_quick_action_confirmed', props);
}

// ─── Video Templates ─────────────────────────────────────────────

// NOTE: payload key is `action_source` (not `source`) because PostHog's
// initializer registers `source: 'frontend'` as a super-property and
// re-sets it in capture() — using `source` here would be silently
// overwritten. See PLAT-9680 follow-up.
export type VideoTemplateActionSource =
  | 'produce_modal'
  | 'content_studio_popup'
  | 'creator_studio';

export function trackVideoTemplateApplied(props: {
  action_source: VideoTemplateActionSource;
  template_id: string;
  template_name?: string;
  clip_id?: string;
  clip_title?: string;
  story_id?: string;
  story_title?: string;
  video_id?: string;
  video_title?: string;
  previous_template_id?: string | null;
  previous_template_name?: string | null;
}): void {
  track('video_template_applied', props);
}

// ─── What's New ──────────────────────────────────────────────────

export function trackWhatsNewOpened(props: { had_new_release: boolean }): void {
  track('whats_new_opened', props);
}

// ─── Transcript Actions ──────────────────────────────────────────

// NOTE: payload key is `action_source` (not `source`) because PostHog's
// initializer registers `source: 'frontend'` as a super-property and
// re-sets it in capture() — using `source` here would be silently
// overwritten. See PLAT-9680 follow-up.
export type TranscriptActionSource =
  | 'content_studio_popup'
  | 'content_studio_transcript_tab'
  | 'creator_studio';

export function trackSaveClipFromTranscript(props: {
  action_source: TranscriptActionSource;
  story_id?: string;
  story_title?: string;
  video_id?: string;
  video_title?: string;
  selected_word_count: number;
  selected_text_preview: string;
}): void {
  track('save_clip_from_transcript', props);
}

/**
 * NOTE: Old event name preserved for data continuity. This replaces the
 * pre-existing `analytics.track('save_pull_quote_from_transcript', ...)` call.
 *
 * `speaker_label`, `speaker_name`, and `saved_id` were on the legacy
 * Segment payload; preserved here so existing dashboards keep working.
 */
export function trackSavePullQuoteFromTranscript(props: {
  action_source: TranscriptActionSource;
  story_id?: string;
  story_title?: string;
  video_id?: string;
  video_title?: string;
  selected_word_count: number;
  selected_text_preview: string;
  speaker_label?: string;
  speaker_name?: string;
  saved_id?: string;
}): void {
  track('save_pull_quote_from_transcript', props);
}

export function trackReplaceTranscriptText(props: {
  action_source: TranscriptActionSource;
  story_id?: string;
  story_title?: string;
  video_id?: string;
  original_text: string;
  replacement_text: string;
  is_multi_range: boolean;
  match_count: number;
}): void {
  track('replace_transcript_text', props);
}

// ─── Translation Memory ──────────────────────────────────────────

/**
 * PLAT-10853 (epic PLAT-10825) — workspace translation-memory instrumentation.
 *
 * Two conventions hold across this whole section:
 *
 * - **No glossary text.** Source terms and translations are customer content
 *   (brand names, product nomenclature, unreleased naming) — only counts,
 *   BCP-47 language codes, collection ids and booleans ship. `is_dnt` stands in
 *   for the do-not-translate case (E4: `translation === sourceTerm`).
 * - **One event per write site**, so the totals compose without overlap: an
 *   entry is born via `_entry_created` (Settings), `_promoted_from_manual`
 *   (Translation Review), or the `created` count inside `_imported` — never two
 *   of them. The one deliberate exception is `_moved`, which is a strict subset
 *   of `_updated` (see `trackTmEntryMoved`).
 *
 * `collection_id` is `null` for the Default (uncategorized) bucket, and
 * `target_lang` is `null` for an entry that applies to every target language.
 */

/** Settings → Add entry. Import and promote have their own events. */
export function trackTmEntryCreated(props: {
  showcase_id?: string;
  collection_id: string | null;
  source_lang?: string;
  target_lang?: string | null;
  is_dnt: boolean;
  has_comment: boolean;
}): void {
  track('translation_memory_entry_created', props);
}

export type TmEntrySurface = 'settings' | 'translation_review';

/** Every entry edit, from either surface — a complete count of saves. */
export function trackTmEntryUpdated(props: {
  showcase_id?: string;
  collection_id: string | null;
  source_lang?: string;
  target_lang?: string | null;
  is_dnt: boolean;
  has_comment: boolean;
  surface: TmEntrySurface;
}): void {
  track('translation_memory_entry_updated', props);
}

/**
 * Collection reassignment. There is no separate "move" action in the UI — it
 * happens through the same edit modal as any other field — so this fires
 * *alongside* `_updated`, as a strict subset that can be sliced on its own.
 */
export function trackTmEntryMoved(props: {
  showcase_id?: string;
  from_collection_id: string | null;
  to_collection_id: string | null;
  surface: TmEntrySurface;
}): void {
  track('translation_memory_entry_moved', props);
}

/** One per entry — bulk delete loops the same handler, so it fires N times. */
export function trackTmEntryDeleted(props: {
  showcase_id?: string;
  collection_id: string | null;
}): void {
  track('translation_memory_entry_deleted', props);
}

export type TmCollectionSurface = 'collection_list' | 'import_modal';

export function trackTmCollectionCreated(props: {
  showcase_id?: string;
  collection_id: string;
  has_description: boolean;
  surface: TmCollectionSurface;
}): void {
  track('translation_memory_collection_created', props);
}

/** `entry_count` is the collection's entries as of just before the delete. */
export function trackTmCollectionDeleted(props: {
  showcase_id?: string;
  collection_id: string;
  entry_count: number;
}): void {
  track('translation_memory_collection_deleted', props);
}

/**
 * One event per confirmed import, not per row. Counts are the **actual** write
 * results (PLAT-10871), not the preview's optimistic tally. `file_count` is 1
 * on the single-file flow (PLAT-10848) and is in the shape now so bulk import
 * (PLAT-10849) fills it in without changing the event.
 */
export function trackTmImported(props: {
  showcase_id?: string;
  file_count: number;
  row_count: number;
  created: number;
  overwritten: number;
  skipped: number;
  failed: number;
  invalid_rows: number;
  source_lang?: string;
  target_lang?: string | null;
  collection_count: number;
}): void {
  track('translation_memory_imported', props);
}

/** Translation Review → "Save to Translation Memory" (E15, `origin=promoted`). */
export function trackTmPromotedFromManual(props: {
  showcase_id?: string;
  collection_id: string | null;
  source_lang?: string;
  target_lang?: string | null;
  is_dnt: boolean;
}): void {
  track('translation_memory_promoted_from_manual', props);
}

/**
 * Fires once per translation run that actually honored glossary terms, right
 * after `translation_completed`. `entry_count` is the distinct honored entry
 * ids across the returned phrases (E7 — `tmEntryIds` is honored-only, so this
 * counts what the backend QA layer confirmed, not what merely matched).
 *
 * Deliberately NOT fired at zero: a workspace with no glossary would otherwise
 * emit an empty event on every translation and drown the signal. `phrase_count`
 * rides along so adoption reads as a rate rather than a raw count.
 */
export function trackTmApplied(props: {
  story_id?: string;
  showcase_id?: string;
  source_lang: string;
  target_lang: string;
  entry_count: number;
  phrase_count?: number;
  action: 'generate' | 'regenerate';
}): void {
  track('translation_memory_applied', props);
}

/**
 * PLAT-10924 — QA quality, not adoption. Fires once per run in which the backend
 * had to retry at least one segment because a glossary term was reliably missed
 * on the first pass. `rescued_count` is the subset the reinforced retry landed,
 * so `retried - rescued` is what the retry could not save.
 *
 * Skipped at zero, like `translation_memory_applied`: a clean run must not emit
 * an empty event on every translation.
 */
export function trackTmQaRetry(props: {
  story_id?: string;
  showcase_id?: string;
  source_lang: string;
  target_lang: string;
  retried_count: number;
  rescued_count: number;
  action: 'generate' | 'regenerate';
}): void {
  track('translation_memory_qa_retry', props);
}

/**
 * PLAT-10924 — fires once per run that ended with at least one *reliably*
 * unhonored glossary term (E5: a DNT term absent from the output on any path, or
 * a translate-as term the model's self-report contradicted). Terms the QA layer
 * could not verify either way are NOT failures and are not counted here.
 *
 * `error_message` is synthesized from the counts — a missed term throws nothing,
 * and the term text itself is customer content that never ships in an event. The
 * terms live in the backend's `[translation/tm] qa report` log.
 */
export function trackTmQaFailed(props: {
  story_id?: string;
  showcase_id?: string;
  source_lang: string;
  target_lang: string;
  missed_count: number;
  error_message: string;
  action: 'generate' | 'regenerate';
}): void {
  track('translation_memory_qa_failed', props);
}

// ─── Clip Comments (Content Studio review) ───────────────────────
//
// Fired from `CommentStore` after the service call succeeds (rolled-back
// optimistic mutations never emit). `content_type` is `'clip'` today — the only
// commentable surface — and widens with the `CommentContentType` enum. Sibling
// to the publish events (PLAT-10015); these feed the review value score.

/** Base payload shared by every comment event. */
type CommentEventBase = {
  content_type: string;
  video_id?: string;
  story_id?: string;
};

/** Author identity carried on the creation events. */
type CommentAuthorProps = {
  is_guest: boolean;
  author_role?: string;
};

/** A new top-level comment stuck (`is_pinned` = attached to a timecode). */
export function trackCommentAdded(
  props: CommentEventBase & CommentAuthorProps & { is_pinned: boolean },
): void {
  track('comment_added', props);
}

/**
 * Per-drawing detail for the canvas-annotation events (PLAT-11296). Tool mix,
 * ink and mark count only — never the stroke points, which are high-volume
 * review content of no analytical value.
 */
export type CommentDrawingProps = {
  /** Distinct tools used, e.g. ['freehand', 'arrow']. */
  tools: string[];
  /** Human-readable ink names used (see `inkName`), not raw hexes. */
  inks: string[];
  stroke_count: number;
};

/** A comment posted carrying drawn marks (fires alongside `comment_added`). */
export function trackCommentDrawingAdded(
  props: CommentEventBase & CommentAuthorProps & CommentDrawingProps,
): void {
  track('comment_drawing_added', props);
}

/** The author re-drew the marks on their own comment (PLAT-11292). */
export function trackCommentDrawingEdited(
  props: CommentEventBase & CommentDrawingProps,
): void {
  track('comment_drawing_edited', props);
}

/** The author removed the marks from their own comment (PLAT-11292). */
export function trackCommentDrawingCleared(props: CommentEventBase): void {
  track('comment_drawing_cleared', props);
}

/** The control-bar toggle hiding/showing drawings was used (PLAT-11291). */
export function trackCommentDrawingVisibilityToggled(
  props: CommentEventBase & { visible: boolean },
): void {
  track('comment_drawing_visibility_toggled', props);
}

/** A reply under an existing thread stuck. */
export function trackCommentReplyAdded(
  props: CommentEventBase & CommentAuthorProps,
): void {
  track('comment_reply_added', props);
}

export function trackCommentResolved(props: CommentEventBase): void {
  track('comment_resolved', props);
}

export function trackCommentUnresolved(props: CommentEventBase): void {
  track('comment_unresolved', props);
}

export function trackCommentEdited(props: CommentEventBase): void {
  track('comment_edited', props);
}

/** How a comment was repositioned on the timeline. */
export type CommentMoveMethod = 'drag' | 'timecode';

/**
 * A comment repositioned in time. Kept separate from `comment_edited` — moving
 * a comment is not a text edit, and dragging the marker vs typing a timecode
 * are distinct affordances we want to compare (`method`). `from_seconds` is
 * `null` when a general comment gets pinned, `to_seconds` when one is unpinned.
 */
export function trackCommentMoved(
  props: CommentEventBase & {
    method: CommentMoveMethod;
    from_seconds: number | null;
    to_seconds: number | null;
  },
): void {
  track('comment_moved', props);
}

/** `is_top_level` distinguishes a thread delete (cascades to replies) from a
 *  reply delete. */
export function trackCommentDeleted(
  props: CommentEventBase & { is_top_level: boolean },
): void {
  track('comment_deleted', props);
}

/** Fires only when a reaction is applied — un-reacting is not tracked. */
export function trackCommentReactionAdded(
  props: CommentEventBase & { emoji: string },
): void {
  track('comment_reaction_added', props);
}

// ===== Transaction timers (PLAT-11041/11042) =====
// Thin pass-throughs so React components can time transactions through the
// same AnalyticsStore the rest of the tracker routes to.

export function startTimer(
  key: string,
  metadata?: Record<string, unknown>,
): void {
  _analyticsStore?.startTimer(key, metadata);
}

export function markTimer(key: string, markName: string): void {
  _analyticsStore?.markTimer(key, markName);
}

export function endTimer(
  key: string,
  metadata?: Record<string, unknown>,
): void {
  _analyticsStore?.endTimer(key, metadata);
}

export function cancelTimer(key: string, reason?: string): void {
  _analyticsStore?.cancelTimer(key, reason);
}

export function reportTimerStillRunning(
  key: string,
  metadata?: Record<string, unknown>,
): void {
  _analyticsStore?.reportTimerStillRunning(key, metadata);
}
